import { create } from 'zustand';
import { TickerMessage, TickerMessageCustom, CreateTickerMessageDto } from '../models/CXModel';
import {
  fetchTickerMessages,
  createTickerMessage,
  updateTickerMessage,
  deleteTickerMessage,
  fetchCustomTickerMessages,
  createCustomTickerMessage,
  updateCustomTickerMessage,
  deleteCustomTickerMessage,
} from '../services/cxService';
import { OfficeService } from '@/apps/office/services/OfficeService';

interface CxTickerMessageStore {
  // Active Messages (displayed in marquee)
  activeMessages: TickerMessage[];
  isLoadingMessages: boolean;
  messagesError: string | null;

  // Custom Templates (for quick shoutouts)
  customTemplates: TickerMessageCustom[];
  isLoadingTemplates: boolean;
  templatesError: string | null;

  // UI State
  isMarqueeVisible: boolean;
  isMarqueeExpanded: boolean;

  // Active Messages Actions
  fetchActiveMessages: (floorPid: string) => Promise<void>;
  createMessage: (messageData: CreateTickerMessageDto) => Promise<void>;
  updateMessage: (id: number, floorPid: string, updates: Partial<TickerMessage>) => Promise<void>;
  deleteMessage: (id: number, floorPid: string) => Promise<void>;

  // Custom Templates Actions
  fetchCustomTemplates: () => Promise<void>;
  createCustomTemplate: (message: string) => Promise<void>;
  updateCustomTemplate: (id: number, message: string) => Promise<void>;
  deleteCustomTemplate: (id: number) => Promise<void>;

  // UI Actions
  toggleMarqueeVisibility: () => void;
  setMarqueeExpanded: (expanded: boolean) => void;

  // Expiry
  pruneExpiredMessages: () => void;

  // Reset
  reset: () => void;
}

export const useCxTickerMessageStore = create<CxTickerMessageStore>((set, get) => ({
  // Initial State
  activeMessages: [],
  isLoadingMessages: false,
  messagesError: null,

  customTemplates: [],
  isLoadingTemplates: false,
  templatesError: null,

  isMarqueeVisible: true,
  isMarqueeExpanded: true,

  // Active Messages Actions
  fetchActiveMessages: async (floorPid: string) => {
    set({ isLoadingMessages: true, messagesError: null });
    try {
      const { items } = await fetchTickerMessages(floorPid, { limit: 100 });
      set({ activeMessages: items, isLoadingMessages: false });
    } catch (error) {
      console.error('Error fetching ticker messages:', error);
      set({
        messagesError: 'Failed to load ticker messages',
        isLoadingMessages: false,
      });
    }
  },

  createMessage: async (messageData: CreateTickerMessageDto) => {
    try {
      const newMessage = await createTickerMessage(messageData);
      const newActiveMessages = [...get().activeMessages, newMessage];
      set({ activeMessages: newActiveMessages });
      OfficeService.getInstance().emitCxTickerMessageActionEvent('created', newActiveMessages);
    } catch (error) {
      console.error('Error creating ticker message:', error);
      throw error;
    }
  },

  updateMessage: async (id: number, floorPid: string, updates: Partial<TickerMessage>) => {
    try {
      const updatedMessage = await updateTickerMessage(id, floorPid, updates);
      const newActiveMessages = get().activeMessages.map((msg) =>
        msg.id === id ? { ...msg, ...updatedMessage } : msg
      );
      set({ activeMessages: newActiveMessages });
      OfficeService.getInstance().emitCxTickerMessageActionEvent('updated', newActiveMessages);
    } catch (error) {
      console.error('Error updating ticker message:', error);
      throw error;
    }
  },

  deleteMessage: async (id: number, floorPid: string) => {
    try {
      await deleteTickerMessage(id, floorPid);
      const newActiveMessages = get().activeMessages.filter((msg) => msg.id !== id);
      set({ activeMessages: newActiveMessages });
      OfficeService.getInstance().emitCxTickerMessageActionEvent('deleted', newActiveMessages);
    } catch (error) {
      console.error('Error deleting ticker message:', error);
      throw error;
    }
  },

  // Custom Templates Actions
  fetchCustomTemplates: async () => {
    set({ isLoadingTemplates: true, templatesError: null });
    try {
      const templates = await fetchCustomTickerMessages();
      set({ customTemplates: templates, isLoadingTemplates: false });
    } catch (error) {
      console.error('Error fetching custom ticker messages:', error);
      set({
        templatesError: 'Failed to load custom templates',
        isLoadingTemplates: false,
      });
    }
  },

  createCustomTemplate: async (message: string) => {
    try {
      const newTemplate = await createCustomTickerMessage(message);
      set((state) => ({
        customTemplates: [...state.customTemplates, newTemplate],
      }));
    } catch (error) {
      console.error('Error creating custom template:', error);
      throw error;
    }
  },

  updateCustomTemplate: async (id: number, message: string) => {
    try {
      const updatedTemplate = await updateCustomTickerMessage(id, message);
      set((state) => ({
        customTemplates: state.customTemplates.map((template) =>
          template.id === id ? updatedTemplate : template
        ),
      }));
    } catch (error) {
      console.error('Error updating custom template:', error);
      throw error;
    }
  },

  deleteCustomTemplate: async (id: number) => {
    try {
      await deleteCustomTickerMessage(id);
      set((state) => ({
        customTemplates: state.customTemplates.filter((template) => template.id !== id),
      }));
    } catch (error) {
      console.error('Error deleting custom template:', error);
      throw error;
    }
  },

  // UI Actions
  toggleMarqueeVisibility: () => {
    set((state) => ({ isMarqueeVisible: !state.isMarqueeVisible }));
  },

  setMarqueeExpanded: (expanded: boolean) => {
    set({ isMarqueeExpanded: expanded });
  },

  // Expiry
  pruneExpiredMessages: () => {
    const now = new Date();
    const current = get().activeMessages;
    const alive = current.filter(
      (msg) => msg.is_permanent || !msg.end_time || new Date(msg.end_time) > now
    );
    if (alive.length !== current.length) {
      set({ activeMessages: alive });
    }
  },

  // Reset
  reset: () => {
    set({
      activeMessages: [],
      isLoadingMessages: false,
      messagesError: null,
      customTemplates: [],
      isLoadingTemplates: false,
      templatesError: null,
      isMarqueeVisible: true,
      isMarqueeExpanded: true,
    });
  },
}));
